import { hashStringAsync } from '../utils/canonical';
import { signMessage } from '../utils/crypto';
import { verifyMessageSignatures } from '../utils/verification';
import { idbGet, idbSet, idbRemove } from '../utils/indexedDbStorage';
import { updatePairPublicKey } from '../utils/pairing';
import {
  getMessagesChiffres,
  getSignatures,
  getKeys,
  postMessageChiffre,
  postSignature,
  postKey,
} from '../utils/relay';
import { encryptWithECDH, decryptWithECDH } from '../utils/encryption';
import { generateUuid } from '../utils/bip32';
import type { RelayConfig } from '../types/identity';
import type { LocalIdentity } from '../types/identity';
import type {
  MessageBase,
  MsgChiffre,
  MsgCle,
  MsgSignature,
  Signature,
} from '../types/message';

const ECDH_ALGO = 'AES-GCM-ECDH';

const POLL_ATTEMPTS = 15;
const POLL_DELAY_MS = 2000;

const IDB_KEY = 'userwallet_pairing_confirm';
const TYPE_MEMBRE_FINALISER = 'membre finaliser';
const VERSION_V1 = '1';
const VERSION_V2 = '2';
const VERSION_LOGICIELLE = '1.0.0';

export interface PairingConfirmedRecord {
  pairLocal: string;
  pairRemote: string;
  hash: string;
  version: number;
}

interface MembreFinaliserMessage {
  uuid: string;
  version: string;
  types: { types_uuid: string[]; types_names_chiffres: string };
  datajson: {
    services_uuid: string[];
    types_uuid: string[];
    pair_a: string;
    pair_b: string;
    timestamp: number;
  };
  timestamp: number;
  liste_relais: string[];
  version_logicielle: string;
}

function sortedPairKey(a: string, b: string): string {
  return [a, b].sort().join('|');
}

function messageUuid(pairLocal: string, pairRemote: string): string {
  return `pairing-${sortedPairKey(pairLocal, pairRemote)}`;
}

async function loadRecords(): Promise<PairingConfirmedRecord[]> {
  const raw = await idbGet(IDB_KEY);
  if (raw === null) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as PairingConfirmedRecord[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

async function saveRecords(records: PairingConfirmedRecord[]): Promise<void> {
  await idbSet(IDB_KEY, JSON.stringify(records));
}

/**
 * Create "membre finaliser" message. Deterministic uuid from pair pair.
 */
export async function createMembreFinaliserMessage(
  pairLocal: string,
  pairRemote: string,
  relays: string[],
): Promise<{ message: MembreFinaliserMessage; hash: string }> {
  const uuid = messageUuid(pairLocal, pairRemote);
  const pairA = sortedPairKey(pairLocal, pairRemote).split('|')[0] as string;
  const pairB = sortedPairKey(pairLocal, pairRemote).split('|')[1] as string;
  const timestamp = Date.now();
  const message: MembreFinaliserMessage = {
    uuid,
    version: VERSION_V1,
    types: {
      types_uuid: [uuid],
      types_names_chiffres: TYPE_MEMBRE_FINALISER,
    },
    datajson: {
      services_uuid: [],
      types_uuid: [uuid],
      pair_a: pairA,
      pair_b: pairB,
      timestamp,
    },
    timestamp,
    liste_relais: relays,
    version_logicielle: VERSION_LOGICIELLE,
  };
  const canonical = JSON.stringify(message);
  const hash = await hashStringAsync(canonical, 'sha256');
  return { message, hash };
}

/**
 * Sign message hash. Returns Signature for relay.
 */
export function signMembreFinaliser(
  hash: string,
  identity: LocalIdentity,
  nonce: string,
): Signature {
  const pk = identity.privateKey;
  if (pk === undefined) {
    throw new Error('Private key required to sign pairing confirmation');
  }
  const messageToSign = `${hash}-${nonce}`;
  const signatureHex = signMessage(messageToSign, pk);
  return {
    hash,
    cle_publique: identity.publicKey,
    signature: signatureHex,
    nonce,
  };
}

async function encryptPairingMessage(
  message: MembreFinaliserMessage,
  recipientPublicKey: string,
  senderIdentity: LocalIdentity,
): Promise<{ encrypted: string; iv: string; senderPublicKey: string }> {
  const pk = senderIdentity.privateKey;
  if (pk === undefined) {
    throw new Error('Private key required to encrypt pairing message');
  }
  const payload = JSON.stringify(message);
  const out = await encryptWithECDH(payload, recipientPublicKey, pk);
  return {
    encrypted: out.encrypted,
    iv: out.iv,
    senderPublicKey: out.publicKey,
  };
}

function buildMsgCle(
  hashMessage: string,
  iv: string,
  senderPublicKey: string,
): MsgCle {
  return {
    hash_message: hashMessage,
    cle_de_chiffrement_message: {
      algo: ECDH_ALGO,
      params: { iv: iv },
    },
    df_ecdh_scannable: senderPublicKey,
  };
}

interface PublishPairingMessageParams {
  relays: RelayConfig[];
  message: MembreFinaliserMessage;
  hash: string;
  recipientPublicKey?: string;
  senderIdentity: LocalIdentity;
}

/**
 * Publish a pairing message (MsgChiffre) to relays.
 * DH obligatoire: encrypt with ECDH and POST MsgCle. recipientPublicKey and senderIdentity required.
 */
async function publishPairingMessage(
  params: PublishPairingMessageParams,
): Promise<void> {
  const { relays, message, hash, recipientPublicKey, senderIdentity } = params;
  // ECDH encryption is optional: if recipientPublicKey is provided, use ECDH; otherwise publish without encryption
  // The public key will be extracted from signatures and stored for future ECDH use
  const enabled = relays.filter((r) => r.enabled);
  if (enabled.length === 0) {
    throw new Error('No enabled relays');
  }
  
  // If recipientPublicKey is provided, use ECDH encryption
  if (recipientPublicKey !== undefined && recipientPublicKey !== '') {
    const pk = senderIdentity.privateKey;
    if (pk === undefined) {
      throw new Error('Clé privée requise pour chiffrer le message de pairing (ECDH).');
    }
    const { encrypted, iv, senderPublicKey } = await encryptPairingMessage(
      message,
      recipientPublicKey,
      senderIdentity,
    );
    const msgCle = buildMsgCle(hash, iv, senderPublicKey);
    const msgChiffre: MsgChiffre = {
      hash,
      message_chiffre: encrypted,
      datajson_public: message.datajson,
    };
    for (const r of enabled) {
      await postMessageChiffre(r.endpoint, msgChiffre);
      await postKey(r.endpoint, msgCle);
    }
  } else {
    // No ECDH: publish message in clear (base64 encoded)
    // The public key will be extracted from signatures and stored for future ECDH use
    const payload = JSON.stringify(message);
    const messageChiffre = btoa(payload);
    const msgChiffre: MsgChiffre = {
      hash,
      message_chiffre: messageChiffre,
      datajson_public: message.datajson,
    };
    for (const r of enabled) {
      await postMessageChiffre(r.endpoint, msgChiffre);
    }
  }
}

interface PublishPairingMessageAndSignatureParams {
  relays: RelayConfig[];
  message: MembreFinaliserMessage;
  hash: string;
  sig: Signature;
  recipientPublicKey?: string;
  senderIdentity: LocalIdentity;
}

/**
 * Publish message and first signature to relays.
 * DH obligatoire: recipientPublicKey and senderIdentity required.
 */
export async function publishPairingMessageAndSignature(
  params: PublishPairingMessageAndSignatureParams,
): Promise<void> {
  const { relays, message, hash, sig, recipientPublicKey, senderIdentity } =
    params;
  await publishPairingMessage({
    relays,
    message,
    hash,
    recipientPublicKey,
    senderIdentity,
  });
  const enabled = relays.filter((r) => r.enabled);
  const msgSig: MsgSignature = { signature: sig };
  for (const r of enabled) {
    await postSignature(r.endpoint, msgSig);
  }
}

/**
 * Publish a signature only (second device adds its signature).
 */
export async function publishPairingSignature(
  relays: RelayConfig[],
  sig: Signature,
): Promise<void> {
  const enabled = relays.filter((r) => r.enabled);
  if (enabled.length === 0) {
    throw new Error('No enabled relays');
  }
  const msgSig: MsgSignature = { signature: sig };
  for (const r of enabled) {
    await postSignature(r.endpoint, msgSig);
  }
}

interface FetchPairingMessageParams {
  relays: RelayConfig[];
  pairLocal: string;
  pairRemote: string;
  start: number;
  end: number;
  senderPublicKey?: string;
  ourIdentity?: LocalIdentity;
}

/**
 * Fetch messages in time window, find "membre finaliser" v1 for our pairs.
 * DH only: ECDH decryption when senderPublicKey and ourIdentity provided. No base64.
 */
export async function fetchPairingMessage(
  params: FetchPairingMessageParams,
): Promise<{ message: MembreFinaliserMessage; hash: string } | null> {
  const {
    relays,
    pairLocal,
    pairRemote,
    start,
    end,
    senderPublicKey,
    ourIdentity,
  } = params;
  const enabled = relays.filter((r) => r.enabled);
  const key = sortedPairKey(pairLocal, pairRemote);
  const useEcdh =
    senderPublicKey !== undefined &&
    senderPublicKey !== '' &&
    ourIdentity?.privateKey !== undefined;

  for (const r of enabled) {
    try {
      const msgs = await getMessagesChiffres(r.endpoint, start, end);
      for (const m of msgs) {
        const pairA = (m.datajson_public as { pair_a?: string }).pair_a;
        const pairB = (m.datajson_public as { pair_b?: string }).pair_b;
        if (pairA === undefined || pairB === undefined) {
          continue;
        }
        const k = sortedPairKey(pairA, pairB);
        if (k !== key) {
          continue;
        }

        if (!useEcdh) {
          continue;
        }
        const keys = await getKeys(r.endpoint, m.hash);
        let decrypted: string | null = null;
        for (const kc of keys) {
          const algo = kc.cle_de_chiffrement_message?.algo;
          const params = kc.cle_de_chiffrement_message?.params as { iv?: string } | undefined;
          const iv = params?.iv;
          const senderPub = kc.df_ecdh_scannable;
          if (algo !== ECDH_ALGO || iv === undefined || senderPub === undefined) {
            continue;
          }
          if (
            senderPub.toLowerCase() !== senderPublicKey.trim().toLowerCase()
          ) {
            continue;
          }
          try {
            decrypted = await decryptWithECDH(
              m.message_chiffre,
              iv,
              senderPub,
              ourIdentity.privateKey as string,
            );
            break;
          } catch {
            continue;
          }
        }
        if (decrypted === null) {
          continue;
        }
        const parsed = JSON.parse(decrypted) as MembreFinaliserMessage;

        if (parsed.types?.types_names_chiffres !== TYPE_MEMBRE_FINALISER) {
          continue;
        }
        if (parsed.version !== VERSION_V1) {
          continue;
        }
        return { message: parsed, hash: m.hash };
      }
    } catch (e) {
      console.error('fetchPairingMessage', r.endpoint, e);
    }
  }
  return null;
}

/**
 * Fetch signatures for a message hash from relays.
 */
export async function fetchSignaturesForHash(
  relays: RelayConfig[],
  hash: string,
): Promise<Signature[]> {
  const enabled = relays.filter((r) => r.enabled);
  const all: Signature[] = [];
  for (const r of enabled) {
    try {
      const list = await getSignatures(r.endpoint, hash);
      for (const { signature } of list) {
        all.push(signature);
      }
    } catch (e) {
      console.error('fetchSignaturesForHash', r.endpoint, e);
    }
  }
  return all;
}

/**
 * Check we have at least two distinct signers (our key + remote).
 */
function hasTwoDistinctSignatures(
  sigs: Signature[],
  ourPublicKey: string,
): boolean {
  const keys = new Set(sigs.map((s) => s.cle_publique));
  return keys.size >= 2 && keys.has(ourPublicKey);
}

/**
 * Build MessageBase-like for verifyMessageSignatures (needs hash).
 */
function messageWithHash(
  msg: MembreFinaliserMessage,
  hash: string,
): MessageBase {
  return {
    ...msg,
    hash: { algo: 'sha256', hash_value: hash },
  };
}

/**
 * Build M2 (version 2) from M (version 1) and publish to relays.
 * Device 1 calls this after receiving remote signature. DH obligatoire.
 */
async function buildAndPublishPairingVersion2(
  relays: RelayConfig[],
  message: MembreFinaliserMessage,
  recipientPublicKey: string,
  senderIdentity: LocalIdentity,
): Promise<void> {
  const m2: MembreFinaliserMessage = {
    ...message,
    version: VERSION_V2,
  };
  const canonical = JSON.stringify(m2);
  const hash2 = await hashStringAsync(canonical, 'sha256');
  await publishPairingMessage({
    relays,
    message: m2,
    hash: hash2,
    recipientPublicKey,
    senderIdentity,
  });
}

/**
 * Store confirmation and return updated records.
 */
export async function storePairingConfirmed(
  pairLocal: string,
  pairRemote: string,
  hash: string,
  version: number,
): Promise<void> {
  const records = await loadRecords();
  const key = sortedPairKey(pairLocal, pairRemote);
  const existing = records.find(
    (r) => sortedPairKey(r.pairLocal, r.pairRemote) === key,
  );
  if (existing !== undefined) {
    return;
  }
  records.push({ pairLocal, pairRemote, hash, version });
  await saveRecords(records);
}

/**
 * True if we have a confirmed pairing for this (local, remote) pair.
 */
export async function getPairingConfirmed(
  pairLocal: string,
  pairRemote: string,
): Promise<boolean> {
  const records = await loadRecords();
  const key = sortedPairKey(pairLocal, pairRemote);
  return records.some((r) => sortedPairKey(r.pairLocal, r.pairRemote) === key);
}

/**
 * Clear all pairing confirmations from IndexedDB (e.g. on "Supprimer").
 */
export async function clearPairingConfirm(): Promise<void> {
  await idbRemove(IDB_KEY);
}

function delay(ms: number): Promise<void> {
  return new Promise((r) => {
    setTimeout(r, ms);
  });
}

interface RunDevice1ConfirmationParams {
  pairLocal: string;
  pairRemote: string;
  identity: LocalIdentity;
  relays: RelayConfig[];
  remotePublicKey?: string;
}

/**
 * Run confirmation flow for device 1: create M, sign, publish, poll for remote sig, store.
 * remotePublicKey: identity public key of device 2, for ECDH encryption. Required (DH obligatoire).
 */
export async function runDevice1Confirmation(
  params: RunDevice1ConfirmationParams,
): Promise<boolean> {
  const { pairLocal, pairRemote, identity, relays, remotePublicKey } = params;
  if (remotePublicKey !== undefined && remotePublicKey !== '') {
    // ECDH encryption enabled when remotePublicKey is provided
  }
  const { message, hash } = await createMembreFinaliserMessage(
    pairLocal,
    pairRemote,
    relays.map((r) => r.endpoint),
  );
  const n = generateUuid();
  const sig = signMembreFinaliser(hash, identity, n);
  await publishPairingMessageAndSignature({
    relays,
    message,
    hash,
    sig,
    recipientPublicKey: remotePublicKey,
    senderIdentity: identity,
  });
  for (let i = 0; i < POLL_ATTEMPTS; i++) {
    if (i > 0) {
      await delay(POLL_DELAY_MS);
    }
    const sigs = await fetchSignaturesForHash(relays, hash);
    const msg = messageWithHash(message, hash);
    const { valid } = verifyMessageSignatures(msg, sigs);
    if (hasTwoDistinctSignatures(valid, identity.publicKey)) {
      // Extract remote public key from signatures
      const remoteSig = valid.find((s) => s.cle_publique !== identity.publicKey);
      const extractedRemotePublicKey = remoteSig?.cle_publique;
      
      // Update pair with remote public key if found
      if (extractedRemotePublicKey !== undefined) {
        updatePairPublicKey(pairRemote, extractedRemotePublicKey);
      }
      
      // Use extracted key or provided key for ECDH
      const keyForEcdh = extractedRemotePublicKey ?? remotePublicKey;
      if (keyForEcdh !== undefined && keyForEcdh !== '') {
        await buildAndPublishPairingVersion2(
          relays,
          message,
          keyForEcdh,
          identity,
        );
      }
      await storePairingConfirmed(pairLocal, pairRemote, hash, 2);
      return true;
    }
  }
  return false;
}

interface RunDevice2ConfirmationParams {
  pairLocal: string;
  pairRemote: string;
  identity: LocalIdentity;
  relays: RelayConfig[];
  start: number;
  end: number;
  remotePublicKey?: string;
}

/**
 * Run confirmation flow for device 2: fetch M, verify sig1, sign, publish sig2, store.
 * remotePublicKey: identity public key of device 1 (sender), for ECDH decryption.
 */
export async function runDevice2Confirmation(
  params: RunDevice2ConfirmationParams,
): Promise<boolean> {
  const {
    pairLocal,
    pairRemote,
    identity,
    relays,
    start,
    end,
    remotePublicKey,
  } = params;
  const found = await fetchPairingMessage({
    relays,
    pairLocal,
    pairRemote,
    start,
    end,
    senderPublicKey: remotePublicKey,
    ourIdentity: identity,
  });
  if (found === null) {
    return false;
  }
  const { message, hash } = found;
  const sigs = await fetchSignaturesForHash(relays, hash);
  const msg = messageWithHash(message, hash);
  const { valid } = verifyMessageSignatures(msg, sigs);
  if (valid.length === 0) {
    return false;
  }
  
  // Extract remote public key from signatures (device 1's signature)
  const remoteSig = valid.find((s) => s.cle_publique !== identity.publicKey);
  const extractedRemotePublicKey = remoteSig?.cle_publique;
  
  // Update pair with remote public key if found
  if (extractedRemotePublicKey !== undefined) {
    updatePairPublicKey(pairRemote, extractedRemotePublicKey);
  }
  
  const n = generateUuid();
  const sig = signMembreFinaliser(hash, identity, n);
  await publishPairingSignature(relays, sig);
  await storePairingConfirmed(pairLocal, pairRemote, hash, 2);
  return true;
}

interface CheckPairingConfirmationFromSyncParams {
  relays: RelayConfig[];
  pairLocal: string;
  pairRemote: string;
  identity: LocalIdentity;
  start: number;
  end: number;
  remotePublicKey?: string;
}

/**
 * Check for pairing confirmation during Sync (device 1 timed out, user runs Sync later).
 * Fetches pairing message v1, signatures; if both signers present, stores confirmation.
 * remotePublicKey: identity public key of the other device (sender), for ECDH decryption.
 */
export async function checkPairingConfirmationFromSync(
  params: CheckPairingConfirmationFromSyncParams,
): Promise<boolean> {
  const {
    relays,
    pairLocal,
    pairRemote,
    identity,
    start,
    end,
    remotePublicKey,
  } = params;
  const already = await getPairingConfirmed(pairLocal, pairRemote);
  if (already) {
    return true;
  }
  const found = await fetchPairingMessage({
    relays,
    pairLocal,
    pairRemote,
    start,
    end,
    senderPublicKey: remotePublicKey,
    ourIdentity: identity,
  });
  if (found === null) {
    return false;
  }
  const { message, hash } = found;
  const sigs = await fetchSignaturesForHash(relays, hash);
  const msg = messageWithHash(message, hash);
  const { valid } = verifyMessageSignatures(msg, sigs);
  if (!hasTwoDistinctSignatures(valid, identity.publicKey)) {
    return false;
  }
  await storePairingConfirmed(pairLocal, pairRemote, hash, 2);
  return true;
}
